import clsx from 'clsx';
import RoundButton from 'Components/RoundButton';
import { exitFullMode } from 'Components/VideoControls/helper';
import { Box, Button } from 'grommet';
import { LoaderCircleIcon } from 'lucide-preact';
import { useEffect } from 'preact/hooks';
import { connect, useDispatch } from 'react-redux';
import { API_BASE_URL } from 'Shared/fetch';
import { logGAEvent } from 'Shared/ga';
import useFetch from 'use-http';

import { TipContent, Tooltip } from '@/components/Tooltip';
import { useIsMobileSize, usePendingUGC, useUGCTooltipMessage } from '@/hooks';
import { setClipData, setIsCreatingUGC } from '@/redux/actions';
import { setClipButtonClicked } from '@/redux/slices/user/actions';
import { showTrimVideoModal } from '@/routes/habitat/components/TrimVideoModal/actions';
import appEvents from '@/shared/appEvents';

import clip from './clip.svg';
import style from './style.scss';

const RecordVideoButton = ({
  isClicked,
  plain,
  streamId,
  slug,
  habitatId,
  fullScreen,
  setClipDataAction,
  showTrimVideoModalAction,
}) => {
  const dispatch = useDispatch();
  const isMobileSize = useIsMobileSize();
  const isPendingUGC = usePendingUGC();
  const tooltipMessage = useUGCTooltipMessage();
  const { loading, data, error, post } = useFetch(API_BASE_URL, {
    credentials: 'include',
    cachePolicy: 'no-cache',
    headers: { 'Content-Type': 'application/json' },
    timeout: 300000,
  });
  const isDisabled = loading || isPendingUGC;
  const tooltipDisabled = !isDisabled || !tooltipMessage || isMobileSize;
  useEffect(() => {
    if (data?.videoURL && data?.duration) {
      logGAEvent('clip-button', 'clip-received', slug);
      setClipDataAction({ ...data, loading: false });
      showTrimVideoModalAction();
      dispatch(setIsCreatingUGC(false));
      // On the clip arriving (its trim modal opening), not on the click: this only moves the
      // onboarding to "now save it". Real success is emitted when the clip is saved.
      appEvents.emit('habitat:media-captured', { habitatId, kind: 'clip' });
      if (fullScreen) {
        exitFullMode();
      }
    }

    if (error) {
      console.error('Error While requesting stream clip', error);
      logGAEvent('clip-button', 'clip-error', slug);
      dispatch(setIsCreatingUGC(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, dispatch]);

  const onClickHandler = () => {
    if (isPendingUGC) {
      return;
    }

    setClipDataAction({ loading: true });
    dispatch(setIsCreatingUGC(true));
    post('/videos/clip', { streamId });
    logGAEvent('clip-button', 'click', slug);
    dispatch(setClipButtonClicked(true));
  };

  if (plain) {
    return (
      <Tooltip
        disabled={tooltipDisabled}
        dropProps={{ align: { bottom: 'top' } }}
        content={tooltipMessage ? <TipContent message={tooltipMessage} /> : undefined}
        plain
      >
        <Button plain onClick={onClickHandler} className={style.mobileButton} disabled={isDisabled}>
          {loading ? (
            <LoaderCircleIcon className="animate-spin" color="#fff" size={22} />
          ) : (
            <img src={clip} alt="Take a clip" />
          )}
        </Button>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      disabled={tooltipDisabled}
      dropProps={{ align: { left: 'right' } }}
      content={tooltipMessage ? <TipContent message={tooltipMessage} /> : undefined}
      plain
    >
      <Box>
        <RoundButton
          onClick={onClickHandler}
          width="36"
          backgroundColor="var(--secondaryGreen)"
          color="white"
          className={clsx(style.button, { [style.animate]: !isClicked })}
          disabled={isDisabled}
        >
          <div className={style.innerContainerClipButton}>
            {loading ? (
              <LoaderCircleIcon className="animate-spin" color="white" size={24} />
            ) : (
              <img src={clip} alt="Take a clip" />
            )}
          </div>
        </RoundButton>
      </Box>
    </Tooltip>
  );
};

export default connect(
  ({
    user: { clipButtonClicked: isClicked },
    habitat: {
      habitatInfo: { _id: habitatId, selectedCamera: { streamKey } = {}, slug: habitatSlug, zoo: { slug: zooSlug } = {} },
    },
    mainStream: { fullScreen },
  }) => ({
    fullScreen,
    isClicked,
    habitatId,
    streamId: streamKey,
    slug: `${zooSlug}/${habitatSlug}`,
  }),
  {
    setClipDataAction: setClipData,
    showTrimVideoModalAction: showTrimVideoModal,
  },
)(RecordVideoButton);
